import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  BriefcaseBusiness,
  Building2,
  Check,
  CheckCircle2,
  GraduationCap,
  LoaderCircle,
  MapPin,
  Send,
  ShieldCheck,
  Store,
  UserRound,
} from 'lucide-react';
import { useLanguage } from '../i18n/LanguageProvider';

const MAIL_ENDPOINT = 'https://api.wonya-soft.com/api/v1/communications/email';
const REGISTRATION_RECIPIENT = 'wonyasoft.sa@gmail.com';

const initialData = {
  profile: 'merchant',
  organizationName: '',
  contactName: '',
  email: '',
  phone: '',
  city: '',
  province: '',
  organizationType: '',
  estimatedUsers: '',
  activity: '',
  locations: '',
  serviceType: '',
  billingModel: '',
  estimatedCustomers: '',
  need: '',
  timeline: '',
  details: '',
  consent: false,
  website: '',
};

const steps = [
  { label: 'Profil', icon: UserRound },
  { label: 'Organisation', icon: Building2 },
  { label: 'Projet', icon: MapPin },
  { label: 'Vérification', icon: ShieldCheck },
];

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const createRegistrationEmail = (data) => {
  const profile = data.profile === 'school'
    ? 'École'
    : data.profile === 'merchant'
      ? 'Marchand'
      : 'Prestataire de services';
  const profileDetails = data.profile === 'school'
    ? [
        ['Type d’établissement', data.organizationType],
        ['Nombre estimé d’élèves/étudiants', data.estimatedUsers],
      ]
    : data.profile === 'merchant'
      ? [
          ['Secteur d’activité', data.activity],
          ['Nombre de points de vente', data.locations],
        ]
      : [
          ['Type de services', data.serviceType],
          ['Mode de facturation', data.billingModel],
          ['Nombre estimé de clients/abonnés', data.estimatedCustomers],
        ];
  const rows = [
    ['Profil', profile],
    ['Organisation', data.organizationName],
    ['Responsable', data.contactName],
    ['Email', data.email],
    ['Téléphone', data.phone],
    ['Ville', data.city],
    ['Province', data.province],
    ...profileDetails,
    ['Besoin principal', data.need],
    ['Délai envisagé', data.timeline],
  ];

  return `
    <div style="background:#f4f5f7;padding:32px 16px;font-family:Arial,sans-serif;color:#111827">
      <div style="max-width:680px;margin:auto;background:#fff;border:1px solid #e5e7eb;border-radius:22px;overflow:hidden">
        <div style="padding:28px 32px;background:linear-gradient(135deg,#c81d25,#8f1118);color:#fff">
          <div style="font-size:12px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;opacity:.85">WonyaSoft · Inscription</div>
          <h1 style="margin:9px 0 0;font-size:25px">Nouvelle demande ${escapeHtml(profile.toLowerCase())}</h1>
        </div>
        <div style="padding:30px">
          <table role="presentation" style="width:100%;border-collapse:collapse;font-size:14px">
            ${rows.map(([label, value]) => `
              <tr>
                <td style="padding:10px 0;color:#6b7280;width:220px;border-bottom:1px solid #f0f1f3">${escapeHtml(label)}</td>
                <td style="padding:10px 0;font-weight:700;border-bottom:1px solid #f0f1f3">${escapeHtml(value || 'Non précisé')}</td>
              </tr>`).join('')}
          </table>
          <div style="margin-top:24px;font-size:12px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#a4141b">Précisions</div>
          <div style="margin-top:10px;padding:18px;background:#f9fafb;border-left:3px solid #c81d25;border-radius:0 12px 12px 0;line-height:1.7">
            ${escapeHtml(data.details || 'Aucune précision').replace(/\n/g, '<br>')}
          </div>
        </div>
      </div>
    </div>`;
};

const RegistrationWizard = () => {
  const { t } = useLanguage();
  const [step, setStep] = useState(0);
  const [data, setData] = useState(initialData);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const panelRef = useRef(null);

  const updateField = ({ target }) => {
    const value = target.type === 'checkbox' ? target.checked : target.value;
    setData((current) => ({ ...current, [target.name]: value }));
    setErrors((current) => ({ ...current, [target.name]: '' }));
  };

  const chooseProfile = (profile) => {
    setData((current) => ({ ...current, profile }));
    setErrors({});
  };

  const requiredFields = () => {
    if (step === 0) return ['profile'];
    if (step === 1) {
      const commonFields = ['organizationName', 'contactName', 'email', 'phone', 'city'];
      if (data.profile === 'school') return [...commonFields, 'organizationType'];
      if (data.profile === 'merchant') return [...commonFields, 'activity'];
      return [...commonFields, 'serviceType', 'billingModel'];
    }
    if (step === 2) return ['need', 'timeline'];
    return ['consent'];
  };

  const validateStep = () => {
    const nextErrors = {};
    requiredFields().forEach((field) => {
      if (!data[field]) nextErrors[field] = t('Ce champ est requis.');
    });
    if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      nextErrors.email = t('Saisissez une adresse email valide.');
    }
    setErrors(nextErrors);
    const firstError = Object.keys(nextErrors)[0];
    if (firstError) {
      window.requestAnimationFrame(() => document.querySelector(`[name="${firstError}"]`)?.focus());
    }
    return !firstError;
  };

  const goNext = () => {
    if (validateStep()) setStep((current) => Math.min(current + 1, steps.length - 1));
  };

  const goBack = () => {
    setErrors({});
    setStep((current) => Math.max(current - 1, 0));
  };

  useEffect(() => {
    panelRef.current?.focus({ preventScroll: true });
  }, [step]);

  const submitRegistration = async () => {
    if (!validateStep()) return;
    setStatus('submitting');
    try {
      if (data.website) {
        setStatus('success');
        return;
      }
      const profileLabel = data.profile === 'school'
        ? 'École'
        : data.profile === 'merchant'
          ? 'Marchand'
          : 'Prestataire de services';
      const response = await fetch(MAIL_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: REGISTRATION_RECIPIENT,
          subject: `[Inscription WonyaSoft] ${profileLabel} — ${data.organizationName}`,
          html: createRegistrationEmail(data),
        }),
      });
      const result = await response.json();
      const accepted = result?.data?.data?.success === true;
      setStatus(response.ok && (result.success === true || accepted) ? 'success' : 'error');
    } catch (error) {
      console.error("Erreur lors de l'envoi de l'inscription:", error);
      setStatus('error');
    }
  };

  const resetWizard = () => {
    setData(initialData);
    setErrors({});
    setStatus('idle');
    setStep(0);
  };

  const fieldError = (name) => errors[name]
    ? <span className="ws-wizard__error" id={`${name}-error`}>{errors[name]}</span>
    : null;

  if (status === 'success') {
    return (
      <div className="ws-wizard ws-wizard--success" role="status">
        <span className="ws-wizard-success__icon"><CheckCircle2 size={38} aria-hidden="true" /></span>
        <span className="ws-kicker">{t('Demande transmise')}</span>
        <h3>{t('Votre demande d’inscription a bien été envoyée.')}</h3>
        <p>{t('Notre équipe examinera les informations et vous contactera pour la prochaine étape.')}</p>
        <button className="ws-button ws-button--secondary" type="button" onClick={resetWizard}>
          {t('Envoyer une autre demande')}
        </button>
      </div>
    );
  }

  return (
    <div className="ws-wizard">
      <ol className="ws-wizard-progress" aria-label={t('Progression de la demande')}>
        {steps.map(({ label, icon: Icon }, index) => (
          <li className={index === step ? 'is-current' : index < step ? 'is-complete' : ''} key={label} aria-current={index === step ? 'step' : undefined}>
            <span>{index < step ? <Check size={18} aria-hidden="true" /> : <Icon size={18} aria-hidden="true" />}</span>
            <small>{t(label)}</small>
          </li>
        ))}
      </ol>

      <div className="ws-wizard__panel" ref={panelRef} tabIndex="-1">
        <div className="ws-wizard__step-count">{t('Étape')} {step + 1} / {steps.length}</div>

        {step === 0 && (
          <>
            <div className="ws-wizard__heading">
              <h3>{t('Quel profil souhaitez-vous inscrire ?')}</h3>
              <p>{t('Votre choix adapte les prochaines questions à votre activité.')}</p>
            </div>
            <div className="ws-wizard-profile-grid">
              {[
                { value: 'school', icon: GraduationCap, title: 'Une école', text: 'Établissement scolaire, institut ou université' },
                { value: 'merchant', icon: Store, title: 'Un marchand', text: 'Commerce, boutique ou point de vente' },
                { value: 'service', icon: BriefcaseBusiness, title: 'Un prestataire de services', text: 'Abonnement, cotisation, forfait ou prestation ponctuelle' },
              ].map(({ value, icon: Icon, title, text }) => (
                <button
                  className={data.profile === value ? 'ws-wizard-profile is-selected' : 'ws-wizard-profile'}
                  type="button"
                  name="profile"
                  aria-pressed={data.profile === value}
                  onClick={() => chooseProfile(value)}
                  key={value}
                >
                  <span><Icon size={28} aria-hidden="true" /></span>
                  <strong>{t(title)}</strong>
                  <small>{t(text)}</small>
                  <i><Check size={16} aria-hidden="true" /></i>
                </button>
              ))}
            </div>
            {fieldError('profile')}
          </>
        )}

        {step === 1 && (
          <>
            <div className="ws-wizard__heading">
              <h3>
                {data.profile === 'school'
                  ? t('Parlez-nous de votre établissement')
                  : data.profile === 'merchant'
                    ? t('Parlez-nous de votre commerce')
                    : t('Parlez-nous de vos services')}
              </h3>
              <p>{t('Ces informations nous permettent de qualifier votre demande.')}</p>
            </div>
            <div className="ws-wizard-fields">
              <label className="ws-field">
                <span>
                  {data.profile === 'school'
                    ? t('Nom de l’établissement')
                    : data.profile === 'merchant'
                      ? t('Nom du commerce')
                      : t('Nom de l’entreprise ou de l’activité')} *
                </span>
                <input name="organizationName" value={data.organizationName} onChange={updateField} aria-invalid={Boolean(errors.organizationName)} aria-describedby={errors.organizationName ? 'organizationName-error' : undefined} autoComplete="organization" />
                {fieldError('organizationName')}
              </label>
              <label className="ws-field">
                <span>{t('Nom du responsable')} *</span>
                <input name="contactName" value={data.contactName} onChange={updateField} aria-invalid={Boolean(errors.contactName)} aria-describedby={errors.contactName ? 'contactName-error' : undefined} autoComplete="name" />
                {fieldError('contactName')}
              </label>
              <label className="ws-field">
                <span>{t('Adresse email')} *</span>
                <input name="email" type="email" value={data.email} onChange={updateField} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'email-error' : undefined} autoComplete="email" />
                {fieldError('email')}
              </label>
              <label className="ws-field">
                <span>{t('Téléphone')} *</span>
                <input name="phone" type="tel" value={data.phone} onChange={updateField} aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? 'phone-error' : undefined} autoComplete="tel" placeholder="+243 XXX XXX XXX" />
                {fieldError('phone')}
              </label>
              <label className="ws-field">
                <span>{t('Ville')} *</span>
                <input name="city" value={data.city} onChange={updateField} aria-invalid={Boolean(errors.city)} aria-describedby={errors.city ? 'city-error' : undefined} autoComplete="address-level2" />
                {fieldError('city')}
              </label>
              <label className="ws-field">
                <span>{t('Province')}</span>
                <input name="province" value={data.province} onChange={updateField} autoComplete="address-level1" />
              </label>
              {data.profile === 'school' && (
                <>
                  <label className="ws-field">
                    <span>{t('Type d’établissement')} *</span>
                    <select name="organizationType" value={data.organizationType} onChange={updateField} aria-invalid={Boolean(errors.organizationType)} aria-describedby={errors.organizationType ? 'organizationType-error' : undefined}>
                      <option value="">{t('Sélectionnez')}</option>
                      <option value="École primaire ou secondaire">{t('École primaire ou secondaire')}</option>
                      <option value="Institut supérieur ou université">{t('Institut supérieur ou université')}</option>
                      <option value="Centre de formation">{t('Centre de formation')}</option>
                    </select>
                    {fieldError('organizationType')}
                  </label>
                  <label className="ws-field">
                    <span>{t('Nombre approximatif d’élèves ou étudiants')}</span>
                    <input name="estimatedUsers" type="number" min="1" inputMode="numeric" value={data.estimatedUsers} onChange={updateField} />
                  </label>
                </>
              )}
              {data.profile === 'merchant' && (
                <>
                  <label className="ws-field">
                    <span>{t('Secteur d’activité')} *</span>
                    <input name="activity" value={data.activity} onChange={updateField} aria-invalid={Boolean(errors.activity)} aria-describedby={errors.activity ? 'activity-error' : undefined} placeholder={t('Ex. alimentation, pharmacie, services')} />
                    {fieldError('activity')}
                  </label>
                  <label className="ws-field">
                    <span>{t('Nombre de points de vente')}</span>
                    <input name="locations" type="number" min="1" inputMode="numeric" value={data.locations} onChange={updateField} />
                  </label>
                </>
              )}
              {data.profile === 'service' && (
                <>
                  <label className="ws-field">
                    <span>{t('Type de services')} *</span>
                    <select name="serviceType" value={data.serviceType} onChange={updateField} aria-invalid={Boolean(errors.serviceType)} aria-describedby={errors.serviceType ? 'serviceType-error' : undefined}>
                      <option value="">{t('Sélectionnez')}</option>
                      <option value="Services professionnels ou conseil">{t('Services professionnels ou conseil')}</option>
                      <option value="Santé, beauté ou bien-être">{t('Santé, beauté ou bien-être')}</option>
                      <option value="Transport ou logistique">{t('Transport ou logistique')}</option>
                      <option value="Immobilier ou location">{t('Immobilier ou location')}</option>
                      <option value="Association, club ou organisation">{t('Association, club ou organisation')}</option>
                      <option value="Services numériques ou contenus">{t('Services numériques ou contenus')}</option>
                      <option value="Autre activité de services">{t('Autre activité de services')}</option>
                    </select>
                    {fieldError('serviceType')}
                  </label>
                  <label className="ws-field">
                    <span>{t('Mode de facturation principal')} *</span>
                    <select name="billingModel" value={data.billingModel} onChange={updateField} aria-invalid={Boolean(errors.billingModel)} aria-describedby={errors.billingModel ? 'billingModel-error' : undefined}>
                      <option value="">{t('Sélectionnez')}</option>
                      <option value="Abonnement récurrent">{t('Abonnement récurrent')}</option>
                      <option value="Paiement par prestation">{t('Paiement par prestation')}</option>
                      <option value="Forfait ou paiement anticipé">{t('Forfait ou paiement anticipé')}</option>
                      <option value="Cotisation ou adhésion">{t('Cotisation ou adhésion')}</option>
                      <option value="Modèle mixte">{t('Modèle mixte')}</option>
                    </select>
                    {fieldError('billingModel')}
                  </label>
                  <label className="ws-field">
                    <span>{t('Nombre approximatif de clients ou abonnés')}</span>
                    <input name="estimatedCustomers" type="number" min="1" inputMode="numeric" value={data.estimatedCustomers} onChange={updateField} />
                  </label>
                </>
              )}
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="ws-wizard__heading">
              <h3>{t('Quel est votre besoin principal ?')}</h3>
              <p>{t('Précisez votre objectif afin que notre équipe prépare le bon accompagnement.')}</p>
            </div>
            <div className="ws-wizard-fields">
              <label className="ws-field">
                <span>{t('Solution recherchée')} *</span>
                <select name="need" value={data.need} onChange={updateField} aria-invalid={Boolean(errors.need)} aria-describedby={errors.need ? 'need-error' : undefined}>
                  <option value="">{t('Sélectionnez')}</option>
                  {data.profile === 'school' && (
                    <>
                      <option value="Paiement des frais scolaires">{t('Paiement des frais scolaires')}</option>
                      <option value="Suivi des paiements">{t('Suivi des paiements')}</option>
                      <option value="Solution complète pour établissement">{t('Solution complète pour établissement')}</option>
                    </>
                  )}
                  {data.profile === 'merchant' && (
                    <>
                      <option value="Encaissement Mobile Money">{t('Encaissement Mobile Money')}</option>
                      <option value="Paiement par QR Code">{t('Paiement par QR Code')}</option>
                      <option value="Gestion de plusieurs points de vente">{t('Gestion de plusieurs points de vente')}</option>
                    </>
                  )}
                  {data.profile === 'service' && (
                    <>
                      <option value="Encaissement des abonnements ou cotisations">{t('Encaissement des abonnements ou cotisations')}</option>
                      <option value="Facturation et rappels de paiement">{t('Facturation et rappels de paiement')}</option>
                      <option value="Paiement de prestations par lien ou QR Code">{t('Paiement de prestations par lien ou QR Code')}</option>
                      <option value="Suivi des clients et des échéances">{t('Suivi des clients et des échéances')}</option>
                    </>
                  )}
                  <option value="Autre besoin">{t('Autre besoin')}</option>
                </select>
                {fieldError('need')}
              </label>
              <label className="ws-field">
                <span>{t('Délai envisagé')} *</span>
                <select name="timeline" value={data.timeline} onChange={updateField} aria-invalid={Boolean(errors.timeline)} aria-describedby={errors.timeline ? 'timeline-error' : undefined}>
                  <option value="">{t('Sélectionnez')}</option>
                  <option value="Dès que possible">{t('Dès que possible')}</option>
                  <option value="Dans un à trois mois">{t('Dans un à trois mois')}</option>
                  <option value="Plus tard ou à définir">{t('Plus tard ou à définir')}</option>
                </select>
                {fieldError('timeline')}
              </label>
              <label className="ws-field ws-field--wide">
                <span>{t('Informations complémentaires')}</span>
                <textarea name="details" value={data.details} onChange={updateField} rows="5" placeholder={t('Décrivez brièvement votre fonctionnement actuel et le résultat attendu.')} />
                <small>{t('N’incluez jamais de mot de passe, de code PIN ou de secret API.')}</small>
              </label>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div className="ws-wizard__heading">
              <h3>{t('Vérifiez votre demande')}</h3>
              <p>{t('Relisez les informations avant l’envoi à l’équipe WonyaSoft.')}</p>
            </div>
            <dl className="ws-wizard-summary">
              <div>
                <dt>{t('Profil')}</dt>
                <dd>
                  {data.profile === 'school'
                    ? t('École')
                    : data.profile === 'merchant'
                      ? t('Marchand')
                      : t('Prestataire de services')}
                </dd>
              </div>
              <div><dt>{t('Organisation')}</dt><dd>{data.organizationName}</dd></div>
              <div><dt>{t('Responsable')}</dt><dd>{data.contactName}</dd></div>
              <div><dt>{t('Coordonnées')}</dt><dd>{data.email}<br />{data.phone}</dd></div>
              <div><dt>{t('Localisation')}</dt><dd>{[data.city, data.province].filter(Boolean).join(', ')}</dd></div>
              <div>
                <dt>
                  {data.profile === 'school'
                    ? t('Type d’établissement')
                    : data.profile === 'merchant'
                      ? t('Secteur d’activité')
                      : t('Mode de facturation')}
                </dt>
                <dd>
                  {data.profile === 'school'
                    ? data.organizationType
                    : data.profile === 'merchant'
                      ? data.activity
                      : data.billingModel}
                </dd>
              </div>
              <div><dt>{t('Besoin')}</dt><dd>{data.need}</dd></div>
              <div><dt>{t('Délai')}</dt><dd>{data.timeline}</dd></div>
            </dl>
            <label className="ws-wizard-consent">
              <input name="consent" type="checkbox" checked={data.consent} onChange={updateField} aria-invalid={Boolean(errors.consent)} aria-describedby={errors.consent ? 'consent-error' : undefined} />
              <span>{t('J’accepte que WonyaSoft utilise ces informations uniquement pour traiter cette demande et me recontacter.')}</span>
            </label>
            {fieldError('consent')}
            <div className="ws-contact-honeypot" aria-hidden="true">
              <label htmlFor="registration-website">Site web</label>
              <input id="registration-website" name="website" value={data.website} onChange={updateField} tabIndex="-1" autoComplete="off" />
            </div>
          </>
        )}

        {status === 'error' && (
          <p className="ws-wizard-submit-error" role="alert">
            {t('L’envoi a échoué. Vérifiez votre connexion puis réessayez, ou contactez-nous par WhatsApp.')}
          </p>
        )}

        <div className="ws-wizard-actions">
          {step > 0 && (
            <button className="ws-button ws-button--ghost" type="button" onClick={goBack} disabled={status === 'submitting'}>
              <ArrowLeft size={18} aria-hidden="true" /> {t('Retour')}
            </button>
          )}
          {step < steps.length - 1 ? (
            <button className="ws-button ws-button--primary" type="button" onClick={goNext}>
              {t('Continuer')} <ArrowRight size={18} aria-hidden="true" />
            </button>
          ) : (
            <button className="ws-button ws-button--primary" type="button" onClick={submitRegistration} disabled={status === 'submitting'}>
              {status === 'submitting'
                ? <><LoaderCircle className="ws-wizard-spinner" size={18} aria-hidden="true" /> {t('Envoi en cours…')}</>
                : <><Send size={18} aria-hidden="true" /> {t('Envoyer la demande d’inscription')}</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegistrationWizard;
